package com.havn.app.core.designsystem

import androidx.compose.material3.ColorScheme
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.darkColorScheme
import androidx.compose.material3.lightColorScheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import com.havn.app.ui.theme.HavnThemeCatalog
import com.havn.app.ui.theme.HavnThemeTokens
import com.havn.app.ui.theme.HavnThemeType
import com.havn.app.ui.theme.LocalHavnThemeTokens

val HavnBackground = Color(0xFF05080D)
val HavnSurface = Color(0xFF0B1118)
val HavnSurfaceElevated = Color(0xFF111A24)
val HavnCard = Color(0xFF101820)
val HavnCardBorder = Color(0xFF24313D)
val HavnGold = Color(0xFFD6AA4F)
val HavnGoldLight = Color(0xFFF2D28A)
val HavnGoldDark = Color(0xFF9C742A)
val HavnTextPrimary = Color(0xFFF7F2E8)
val HavnTextSecondary = Color(0xFFB6B0A5)
val HavnTextMuted = Color(0xFF77736B)
val HavnSuccess = Color(0xFF42C77B)
val HavnWarning = Color(0xFFD99A32)
val HavnDanger = Color(0xFFE85D4A)
val HavnCold = Color(0xFF4A78D8)

val HavnBlack = HavnBackground
val HavnCharcoal = HavnSurface
val HavnGraphite = HavnSurfaceElevated
val HavnIvory = HavnTextPrimary
val HavnMuted = HavnTextSecondary
val HavnDeepGold = HavnGoldDark

object HavnSpacing {
    val xs = 4.dp
    val sm = 8.dp
    val md = 16.dp
    val lg = 24.dp
    val xl = 32.dp
    val xxl = 48.dp
}

object HavnListDefaults {
    val headerToSearchGap = 14.dp
    val searchToFilterGap = 10.dp
    val filterToContentGap = 18.dp
    val filterChipGap = 8.dp
    val filterChipHeight = 34.dp
    val rowSeparatorInset = 52.dp
}

private fun havnColorScheme(tokens: HavnThemeTokens): ColorScheme {
    return if (tokens.isLight) {
        lightColorScheme(
            primary = tokens.primaryAccent,
            onPrimary = tokens.primaryAccentContent,
            secondary = tokens.secondaryAccent,
            onSecondary = tokens.primaryAccentContent,
            tertiary = tokens.success,
            background = tokens.background,
            onBackground = tokens.primaryText,
            surface = tokens.surface,
            onSurface = tokens.primaryText,
            surfaceVariant = tokens.card,
            onSurfaceVariant = tokens.secondaryText,
            outline = tokens.border,
            error = tokens.danger,
            onError = tokens.primaryText
        )
    } else {
        darkColorScheme(
            primary = tokens.primaryAccent,
            onPrimary = tokens.primaryAccentContent,
            secondary = tokens.secondaryAccent,
            onSecondary = tokens.primaryAccentContent,
            tertiary = tokens.success,
            background = tokens.background,
            onBackground = tokens.primaryText,
            surface = tokens.surface,
            onSurface = tokens.primaryText,
            surfaceVariant = tokens.card,
            onSurfaceVariant = tokens.secondaryText,
            outline = tokens.border,
            error = tokens.danger,
            onError = tokens.primaryText
        )
    }
}

@Composable
fun HavnTheme(
    themeType: HavnThemeType = HavnThemeType.OBSIDIAN_GOLD,
    content: @Composable () -> Unit
) {
    val tokens = HavnThemeCatalog.tokensFor(themeType)
    CompositionLocalProvider(LocalHavnThemeTokens provides tokens) {
        MaterialTheme(
            colorScheme = havnColorScheme(tokens),
            typography = havnTypography(),
            shapes = havnShapes(),
            content = content
        )
    }
}
